import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, useAuth } from "@/contexts/AuthContext";
import { AdminFeeReport } from "@/components/AdminFeeReport";
import {
  ArrowLeft, ExternalLink, DollarSign, ShieldCheck, RefreshCw, Loader2, CheckCircle2,
} from "lucide-react";

// Sibling commissions workspace. We iframe each of its pages inside our nav so
// the user perceives a single app. The origin is fetched at runtime from
// /api/sso/frontend-config so a super-admin can flip to a white-labeled host
// (e.g. commissions.awgadmin.com) from Admin → SSO without a redeploy.
const FALLBACK_DEALERCOMM_ORIGIN =
  process.env.REACT_APP_DEALERCOMM_ORIGIN ||
  "https://dealercomm.preview.emergentagent.com";

// Which origins we accept postMessages from. Emergent's edge sometimes serves
// the sibling's real document from a different subdomain than the canonical
// routing shell (e.g. `dealercomm.emergent.host` routes but the real doc
// lives on `xyz.emergent.host`). We accept:
//   * the exact canonical origin,
//   * the preview.static twin (Emergent preview quirk),
//   * any origin sharing the same parent (eTLD+1) as the canonical — this
//     is safe because the sibling cryptographically verifies our token
//     with its own secret; a rogue origin cannot forge one.
function _parentDomain(host) {
  // Strip the left-most subdomain label if there are 3+ parts. Good enough
  // for `emergent.host`, `awgadmin.com`, `preview.emergentagent.com`.
  const parts = host.split(".");
  if (parts.length < 3) return host;
  return parts.slice(1).join(".");
}
function buildAllowedOrigins(canonical) {
  const set = new Set([canonical]);
  try {
    const u = new URL(canonical);
    if (u.hostname.endsWith(".preview.emergentagent.com")) {
      set.add(`${u.protocol}//${u.hostname.replace(".preview.emergentagent.com", ".preview.static.emergentagent.com")}`);
    }
  } catch (e) { /* ignore */ }
  return set;
}
function isTrustedOrigin(origin, canonical) {
  const set = buildAllowedOrigins(canonical);
  if (set.has(origin)) return true;
  try {
    const cu = new URL(canonical);
    const ou = new URL(origin);
    // Same-scheme + same parent domain (e.g. any `*.emergent.host` when
    // canonical is `dealercomm.emergent.host`).
    return ou.protocol === cu.protocol && _parentDomain(ou.hostname) === _parentDomain(cu.hostname);
  } catch (e) {
    return false;
  }
}
// Cold-start on Emergent can take 20+ seconds — give the sibling's SsoBridge
// a generous window before surfacing the manual-signin fallback.
const SSO_IDLE_TIMEOUT_MS = 30000;

const ROUTE_MAP = {
  "dashboard":            "/dashboard",
  "agencies-dashboard":   "/dashboard/agencies",
  "statements":           "/commissions",
  "reports":              "/reports",
  "reports/upload":       "/reports/upload",
  "reports/preview":      "/reports/preview",
  "dealers":              "/dealers",
  "dealers/import":       "/dealers/import",
  "products":             "/products",
  "product-types":        "/product-types",
  "product-map":          "/product-map",
  "commission-rules":     "/commission-rules",
  "spiff":                "/spiff",
  "spiff/statement":      "/spiff/statement",
  "spiff/archive":        "/spiff/archive",
  "admin-companies":      "/admin-companies",
};

const TITLES = {
  "dashboard":          "Commissions · Dashboard",
  "agencies-dashboard": "Commissions · Agencies Dashboard",
  "statements":         "Commissions",
  "reports":            "Commissions · Reports",
  "reports/upload":     "Commissions · Upload Report",
  "reports/preview":    "Commissions · Report Preview",
  "dealers":            "Commissions · Dealers",
  "dealers/import":     "Commissions · Import Dealers",
  "products":           "Commissions · Products",
  "product-types":      "Commissions · Product Types",
  "product-map":        "Commissions · Product Mapping",
  "commission-rules":   "Commissions · Commission Rules",
  "spiff":              "Commissions · Spiff",
  "spiff/statement":    "Commissions · Spiff Statement",
  "spiff/archive":      "Commissions · Spiff Archive",
  "admin-companies":    "Commissions · Admin Companies",
};

export default function CommissionsFrame() {
  const { user } = useAuth();
  const location = useLocation();
  const remotePath = useMemo(() => {
    const sub = location.pathname.replace(/^\/commissions\/?/, "");
    return ROUTE_MAP[sub] || "/dashboard";
  }, [location.pathname]);
  const title = useMemo(() => {
    const sub = location.pathname.replace(/^\/commissions\/?/, "");
    return TITLES[sub] || "Commissions";
  }, [location.pathname]);

  const iframeRef = useRef(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [ssoState, setSsoState] = useState("idle"); // idle | ready | authorizing | ok | error
  const [ssoError, setSsoError] = useState(null);
  const [dealercommOrigin, setDealercommOrigin] = useState(FALLBACK_DEALERCOMM_ORIGIN);
  const url = `${dealercommOrigin}${remotePath}`;

  // Handshake guard — prevents the remount loop where dealercomm's SsoBridge
  // re-posts AWG_SSO_READY after its own `window.location.reload()` (post-
  // login rehydrate). Without this, we'd mint a fresh token → they exchange
  // → reload → post ready again → forever. We now respond to at most one
  // READY per iframe instance; if the exchange actually failed downstream,
  // the user hits our Reload button which bumps `iframeKey` and re-arms.
  const handshakeRef = useRef({ iframeKey: -1, mintedAt: 0 });

  // Load the live commissions origin so a super-admin's white-label override
  // takes effect on the next page load (no redeploy).
  useEffect(() => {
    api.get("/sso/frontend-config")
      .then(({ data }) => {
        if (data?.commissions_origin) setDealercommOrigin(data.commissions_origin);
      })
      .catch(() => { /* fallback stays */ });
  }, []);

  // Listen for postMessages from the iframe.
  useEffect(() => {
    let idleTimer = null;
    const onMessage = async (ev) => {
      // Accept the canonical dealercomm origin, its preview.static twin, or
      // any origin sharing the same parent domain. If a message came from a
      // different origin, log once so debugging in prod is a browser-console
      // check away.
      if (!isTrustedOrigin(ev.origin, dealercommOrigin)) {
        if (ev?.data && typeof ev.data === "object" && ev.data.type?.startsWith?.("AWG_SSO")) {
          console.warn("[SSO] Dropping message from untrusted origin:", ev.origin, "canonical:", dealercommOrigin, "msg:", ev.data);
        }
        return;
      }
      const msg = ev?.data;
      if (!msg || typeof msg !== "object") return;
      if (msg.type === "AWG_SSO_READY") {
        // Only honour the FIRST ready per iframe lifecycle. Any subsequent
        // ready is almost certainly dealercomm re-mounting after its own
        // reload — the cookie is already set on that side, so ignoring
        // stops the flicker and the iframe naturally lands on the dashboard.
        if (
          handshakeRef.current.iframeKey === iframeKey &&
          (Date.now() - handshakeRef.current.mintedAt) < 60000
        ) {
          return;
        }
        handshakeRef.current = { iframeKey, mintedAt: Date.now() };
        if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
        setSsoState("authorizing");
        try {
          const { data } = await api.post("/auth/sso/commissions-token");
          // Reply directly to the sender window at its own origin so we don't
          // drop the reply when the iframe is nested under a routing shell.
          if (ev.source && typeof ev.source.postMessage === "function") {
            ev.source.postMessage({ type: "AWG_SSO_TOKEN", token: data.token }, ev.origin);
          } else {
            iframeRef.current?.contentWindow?.postMessage(
              { type: "AWG_SSO_TOKEN", token: data.token },
              ev.origin
            );
          }
        } catch (err) {
          setSsoState("error");
          setSsoError(err?.response?.data?.detail || err?.message || "Failed to mint SSO token");
        }
      } else if (msg.type === "AWG_SSO_OK") {
        setSsoState("ok");
      } else if (msg.type === "AWG_SSO_ERROR") {
        setSsoState("error");
        setSsoError(msg.message || "SSO exchange failed on Commissions side");
      }
    };
    window.addEventListener("message", onMessage);

    // Fallback: if the sibling never announces itself within a few seconds
    // (older deploy, not yet SSO-plumbed, or asleep), surface the manual
    // sign-in affordance so the user isn't stuck on a login form with no
    // feedback.
    idleTimer = setTimeout(() => {
      setSsoState((s) => {
        if (s === "idle") return "error";
        return s;
      });
      setSsoError((prev) => prev || "Commissions workspace hasn't finished loading its SSO handshake — you can sign in manually below, or reload once the sibling is configured");
    }, SSO_IDLE_TIMEOUT_MS);

    return () => {
      window.removeEventListener("message", onMessage);
      if (idleTimer) clearTimeout(idleTimer);
    };
  }, [iframeKey, dealercommOrigin]);

  const reload = () => {
    // Reset the handshake guard so a manual reload can re-arm the flow.
    handshakeRef.current = { iframeKey: -1, mintedAt: 0 };
    setSsoState("idle");
    setSsoError(null);
    setIframeKey((k) => k + 1);
  };

  // Fallback: if the sibling app isn't SSO-plumbed yet, expose a manual sign-in helper.
  const openLoginPopup = () => {
    const w = window.open(`${dealercommOrigin}/login`, "dealercomm-login", "width=520,height=680");
    if (!w) { toast.error("Popup blocked — allow popups for this site."); return; }
    const timer = setInterval(() => {
      if (w.closed) { clearInterval(timer); reload(); toast.success("Session captured — reloading"); }
    }, 500);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col">
      <Navbar />
      <main className="flex-1 flex flex-col max-w-[1600px] mx-auto w-full px-4 sm:px-6 py-6" data-testid="commissions-frame-page">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#6C757D] hover:text-[#0B132B] mb-2">
              <ArrowLeft size={14} /> Back to dashboard
            </Link>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold flex items-center gap-2">
              <DollarSign size={12} /> Commissions module
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-[#0B132B] tracking-tight leading-none mt-1" data-testid="commissions-frame-title">
              {title}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <SsoBadge state={ssoState} />
            {ssoState === "error" && (
              <Button size="sm" variant="outline" onClick={openLoginPopup}
                className="h-9 rounded-sm border-[#0B132B]/20" data-testid="commissions-sso-signin">
                <ShieldCheck size={12} className="mr-1.5" /> Manual sign in
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={reload}
              className="h-9 rounded-sm border-[#0B132B]/20" data-testid="commissions-frame-reload">
              <RefreshCw size={12} className="mr-1.5" /> Reload
            </Button>
            <a href={url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 h-9 px-3 border border-[#0B132B]/20 rounded-sm text-xs font-bold text-[#0B132B] hover:bg-[#F8F9FA]"
              data-testid="commissions-frame-open-new-tab">
              <ExternalLink size={12} /> Open in new tab
            </a>
          </div>
        </div>

        {ssoState === "error" && ssoError && (
          <Card className="p-3 mb-3 border-red-200 bg-red-50 rounded-sm text-xs text-red-800" data-testid="commissions-sso-error">
            <b>True SSO failed:</b> {ssoError}. Use "Manual sign in" as a fallback while the Commissions workspace is being SSO-configured (mirror env: <code>SSO_PARENT_SECRET</code>, <code>SSO_PARENT_ORIGIN</code>).
          </Card>
        )}

        {user?.role === "admin" && <AdminFeeReport />}

        <Card className="flex-1 border-[#E9ECEF] rounded-sm overflow-hidden bg-white min-h-[75vh]">
          <iframe
            key={iframeKey}
            ref={iframeRef}
            src={url}
            title={title}
            className="w-full h-[80vh] border-0 block"
            allow="clipboard-write; clipboard-read"
            data-testid="commissions-frame-iframe"
          />
        </Card>
      </main>
    </div>
  );
}

function SsoBadge({ state }) {
  if (state === "ok") {
    return (
      <span className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm bg-green-50 border border-green-200 text-xs font-bold text-green-800" data-testid="commissions-sso-ok">
        <CheckCircle2 size={12} /> Signed in
      </span>
    );
  }
  if (state === "authorizing") {
    return (
      <span className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm bg-blue-50 border border-blue-200 text-xs font-bold text-blue-800" data-testid="commissions-sso-authorizing">
        <Loader2 size={12} className="animate-spin" /> Signing in…
      </span>
    );
  }
  return null;
}
